import type { ContentHref } from '@generalTypes/apiTypes';
import type { MinimumGoal } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import {
  selectApiWithPendingChangesRelationsToAndFromMap,
  selectBaselineRelationsToAndFromMap,
} from '@newStore/documentApi/documentApiSelectors';
import type { RelationsToAndFromMap } from '@newStore/documentApi/documentApiTypes';
import { stringDiffArrowFunction } from '@newStore/documentUI/transformProposal/asideDiffText';
import { pathMap } from '@newStore/externalData/externalDataTypes';
import { createTypedSelector } from '@newStore/genericHelpers';
import type {
  AsideChangeMessageSelector,
  EditAsideMinimumGoal,
  EditComponent,
} from '@nodeTypeConfig/configTypes';
import { sortBy } from 'lodash';

const isEditAsideMinimumGoal = (config: EditComponent): config is EditAsideMinimumGoal => {
  return config.component === 'minimumGoals' && config.reactComponent === 'AsideMinimumGoal';
};

const getMinimumGoalRelation = (
  config: EditAsideMinimumGoal,
  currentEditingHref: ContentHref,
  minimumGoals: Record<string, MinimumGoal>,
  relationsMap: RelationsToAndFromMap,
) => {
  const allRelations = relationsMap.from[currentEditingHref] || [];

  return allRelations.find(
    (relation) =>
      config.options.relationTypes.includes(relation.relationtype) &&
      !!minimumGoals[relation.to.href],
  );
};

export const selectMinimumGoals = (state: RootState) =>
  state.externalData.data[pathMap.minimumGoals].items;

export const selectMinimumGoalOptions = createTypedSelector(
  [(state: RootState) => selectMinimumGoals(state)],
  (minimumGoals) => {
    const parseUniqueCode = (code: string) => {
      const [prefix, rest] = code.split('-');
      const parts = (rest || '').split('.').map(Number);
      return { prefix: prefix === 'K' ? 0 : Number(prefix), parts };
    };

    const sortedGoals = sortBy<MinimumGoal>(Object.values(minimumGoals), [
      (goal) => {
        const { prefix, parts } = parseUniqueCode(goal.uniqueCode);
        return [prefix, parts[0] || 0, parts[1] || 0, parts[2] || 0];
      },
    ]);

    const options = sortedGoals.map((goal: MinimumGoal) => ({
      href: goal.$$meta.permalink,
      name: `${goal.uniqueCode} - ${goal.title.replace(/<[^>]*>/g, '')} ${goal.description ? `${goal.description}` : ''}`,
    }));
    return options;
  },
);

export const selectCurrentMinimumGoalRelation = createTypedSelector(
  [
    (state: RootState) => selectMinimumGoals(state),
    (state: RootState) => selectApiWithPendingChangesRelationsToAndFromMap(state),
    (_state: RootState, href: ContentHref) => href,
    (_state: RootState, _href: ContentHref, config: EditComponent) => config,
  ],
  (minimumGoals, relationsMap, currentEditingHref, config) => {
    if (!isEditAsideMinimumGoal(config)) {
      return null;
    }
    return getMinimumGoalRelation(config, currentEditingHref, minimumGoals, relationsMap);
  },
);

export const selectChangeMessageForMinimumGoals: AsideChangeMessageSelector<EditAsideMinimumGoal> =
  createTypedSelector(
    [
      (state: RootState) => selectMinimumGoals(state),
      (state: RootState) => selectApiWithPendingChangesRelationsToAndFromMap(state),
      (state: RootState) => selectBaselineRelationsToAndFromMap(state),
      (state: RootState, href: ContentHref) => href,
      (state: RootState, href: ContentHref, config: EditAsideMinimumGoal) => config,
    ],
    (minimumGoals, relationsMap, rawRelationsMap, href, config): string | null => {
      const newRelation = getMinimumGoalRelation(config, href, minimumGoals, relationsMap);
      const oldRelation = getMinimumGoalRelation(config, href, minimumGoals, rawRelationsMap);

      const newValue = newRelation ? minimumGoals[newRelation.to.href].uniqueCode : '';
      const oldValue = oldRelation ? minimumGoals[oldRelation.to.href].uniqueCode : '';

      return stringDiffArrowFunction(newValue, oldValue);
    },
  );
